import { useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";

import { OutcomeScenariosDocument } from "$/__generated__/graphql";

import { OutcomeScenariosContext } from "./context";
import type { OutcomeScenarioOption, ParsedSelectedScenario } from "./types";
import { RISK_ROUTE_TABLES } from "constants/routing";

export const OutcomeScenariosProvider = ({
  children,
}: React.PropsWithChildren) => {
  const navigate = useNavigate();

  const scenariosResult = useQuery(OutcomeScenariosDocument);

  const { outcomeScenarios } = scenariosResult.data ?? {};

  const scenarios = useMemo<Array<OutcomeScenarioOption>>(() => {
    if (outcomeScenarios) {
      return outcomeScenarios.reduce<Array<OutcomeScenarioOption>>(
        (acc, scenario) => {
          for (const effect of scenario.failureEffects) {
            if (effect.hasData) {
              acc.push({
                value: `${scenario.id}_${effect.id}`,
                children: `${scenario.value}, ${effect.value}`,
              });
            }
          }

          return acc;
        },
        [],
      );
    }

    return [];
  }, [outcomeScenarios]);

  const [selectedScenario, setSelectedScenario] = useState<string | undefined>(
    undefined,
  );

  const [parsedScenario, setParsedScenario] =
    useState<ParsedSelectedScenario>();

  const onScenarioSelect = useCallback(
    (value: string) => {
      setSelectedScenario(value);

      const [failureModeId, failureEffectId] = value.split("_");

      setParsedScenario((state) => {
        const failureModeIdNumber = Number(failureModeId);
        const failureEffectIdNumber = Number(failureEffectId);

        if (state) {
          if (
            state[0] !== failureModeIdNumber ||
            state[1] !== failureEffectIdNumber
          ) {
            return [failureModeIdNumber, failureEffectIdNumber];
          }

          return state;
        }

        return [failureModeIdNumber, failureEffectIdNumber];
      });

      navigate(generatePath(`${RISK_ROUTE_TABLES}/${value}`));
    },
    [navigate],
  );

  const value = useMemo(
    () => ({
      ...scenariosResult,
      scenarios,
      selectedScenario,
      setSelectedScenario,
      parsedScenario,
      setParsedScenario,
      onScenarioSelect,
    }),
    [
      scenariosResult,
      scenarios,
      selectedScenario,
      parsedScenario,
      onScenarioSelect,
    ],
  );

  return (
    <OutcomeScenariosContext.Provider value={value}>
      {children}
    </OutcomeScenariosContext.Provider>
  );
};

export default OutcomeScenariosProvider;
